/**
 * FeedbackRatingCard Component
 *
 * Reusable card component for displaying feedback ratings with breakdown visualization.
 * Features:
 * - Rating icon and title
 * - Feedback submission rate (X / Y Tickets)
 * - Large average rating display with gradient
 * - Rating breakdown with progress bars and percentages
 * - Skeleton loading state
 * - Responsive design
 */
import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  SentimentVerySatisfied,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
  SentimentVeryDissatisfied,
} from "@mui/icons-material";
import { SvgIconComponent } from "@mui/icons-material";
import { boxShadows } from "../utils/theme";
import { ICON_SIZE } from "../utils/themeConstants";

interface RatingBreakdown {
  excellent: number;
  good: number;
  average: number;
  poor: number;
  terrible: number;
}

interface FeedbackRatingCardProps {
  /** Title of the card (e.g., "Mall Experience", "Parking Experience") */
  title: string;
  /** Icon component to display */
  icon: SvgIconComponent;
  /** Icon background gradient */
  iconGradient: string;
  /** Icon box shadow */
  iconShadow: string;
  /** Total feedback count */
  totalFeedback: number;
  /** Total tickets count */
  totalTickets: number;
  /** Average rating value */
  averageRating: number;
  /** Rating breakdown object */
  ratingBreakdown: RatingBreakdown;
  /** Chip background color */
  chipBgColor: string;
  /** Chip text color */
  chipTextColor: string;
  /** Chip border color */
  chipBorderColor: string;
  /** Rating display gradient */
  ratingGradient: string;
  /** Rating box background gradient */
  ratingBoxGradient: string;
  /** Rating box border color */
  ratingBoxBorderColor: string;
  /** Hover border color */
  hoverBorderColor: string;
  /** Loading state */
  isLoading?: boolean;
}

const FeedbackRatingCard: React.FC<FeedbackRatingCardProps> = ({
  title,
  icon: Icon,
  iconGradient,
  iconShadow,
  totalFeedback,
  totalTickets,
  averageRating,
  ratingBreakdown,
  chipBgColor,
  chipTextColor,
  chipBorderColor,
  ratingGradient,
  ratingBoxGradient,
  ratingBoxBorderColor,
  hoverBorderColor,
  isLoading = false,
}) => {
  // Helper to get rating icon
  const getRatingIcon = (
    rating: string,
    size: "small" | "medium" = "medium"
  ) => {
    const ratingLower = rating.toLowerCase();
    if (ratingLower === "excellent")
      return (
        <SentimentVerySatisfied fontSize={size} sx={{ color: "#4CAF50" }} />
      );
    if (ratingLower === "good")
      return <SentimentSatisfied fontSize={size} sx={{ color: "#8BC34A" }} />;
    if (ratingLower === "average")
      return <SentimentNeutral fontSize={size} sx={{ color: "#FF9800" }} />;
    if (ratingLower === "poor")
      return (
        <SentimentDissatisfied fontSize={size} sx={{ color: "#FF5722" }} />
      );
    return (
      <SentimentVeryDissatisfied fontSize={size} sx={{ color: "#F44336" }} />
    );
  };

  // Helper to get rating bar color
  const getRatingColor = (rating: string) => {
    const ratingLower = rating.toLowerCase();
    if (ratingLower === "excellent") return "#10b981";
    if (ratingLower === "good") return "#3b82f6";
    if (ratingLower === "average") return "#f59e0b";
    if (ratingLower === "poor") return "#f97316";
    return "#ef4444";
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.08)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: boxShadows.medium,
          borderColor: hoverBorderColor,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {isLoading ? (
          <>
            {/* Skeleton Loading State */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Skeleton variant="rounded" width={40} height={40} />
                <Skeleton
                  variant="text"
                  width={150}
                  sx={{ fontSize: "1.25rem" }}
                />
              </Box>
              <Skeleton variant="rounded" width={80} height={24} />
            </Box>
            <Skeleton
              variant="rounded"
              width="100%"
              height={140}
              sx={{ mb: 3, borderRadius: 3 }}
            />
            {[1, 2, 3, 4, 5].map((i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Skeleton variant="text" width={100} />
                <Skeleton variant="rounded" sx={{ flex: 1, height: 24 }} />
                <Skeleton variant="text" width={40} />
              </Box>
            ))}
          </>
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: iconGradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: iconShadow,
                  }}
                >
                  <Icon sx={{ fontSize: ICON_SIZE.MD, color: "white" }} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  {title}
                </Typography>
              </Box>
              <Chip
                label={`${totalFeedback} / ${totalTickets} Tickets (${
                  totalTickets > 0
                    ? ((totalFeedback / totalTickets) * 100).toFixed(1)
                    : 0
                }%)`}
                size="small"
                sx={{
                  bgcolor: chipBgColor,
                  color: chipTextColor,
                  fontWeight: 600,
                  border: `1px solid ${chipBorderColor}`,
                }}
              />
            </Box>

            {/* Average Rating Display */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                p: 3,
                background: ratingBoxGradient,
                borderRadius: 3,
                border: "2px solid",
                borderColor: ratingBoxBorderColor,
              }}
            >
              <Icon
                sx={{
                  fontSize: ICON_SIZE.HERO,
                  color: chipTextColor,
                  mr: 2,
                }}
              />
              <Box>
                <Typography
                  variant="h2"
                  fontWeight={700}
                  sx={{
                    background: ratingGradient,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {averageRating.toFixed(1)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Average Rating
                </Typography>
              </Box>
            </Box>

            {/* Rating Breakdown */}
            <Grid container spacing={2}>
              {Object.entries(ratingBreakdown).map(([rating, count]) => (
                <Grid item xs={12} key={rating}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 100,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {getRatingIcon(rating, "small")}
                      <Typography
                        variant="body2"
                        sx={{
                          textTransform: "capitalize",
                          fontWeight: 500,
                        }}
                      >
                        {rating}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        height: 24,
                        bgcolor: "grey.200",
                        borderRadius: 2,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${
                            totalFeedback > 0
                              ? (count / totalFeedback) * 100
                              : 0
                          }%`,
                          bgcolor: getRatingColor(rating),
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ minWidth: 35, textAlign: "right" }}
                    >
                      {count}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 45, textAlign: "right" }}
                    >
                      (
                      {totalFeedback > 0
                        ? ((count / totalFeedback) * 100).toFixed(1)
                        : 0}
                      %)
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackRatingCard;
